import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import * as usrLkup from '@admin/user-lookup/user-lookup-grid/user-lookup-grid.columns';
import * as authActions from '@auth/store/auth.actions';
import { ListEntry } from '@data/list-entry';
import { PCache } from '@data/pcache';
import * as invDtl from '@inventory/detail/inv-detail-grid/inv-detail-grid.columns';
import * as invSum from '@inventory/summary/inv-sum-grid/inv-sum-grid.columns';
import * as ordDtl from '@orders/details/ord-dtl-grid/ord-dtl-grid.columns';
import * as ordInq from '@orders/inquiry/ord-inq-grid/ord-inq-grid.columns';
import * as orderActions from '@orders/store/orders.actions';
import * as rptList from '@reports/report-list/report-list-grid/report-list-grid.columns';

import * as tempdataActions from '../tempdata/tempdata.actions';
import * as pcacheActions from './pcache.actions';

export interface OrderFilterState extends EntityState<ListEntry> {}
export const fltOrderFilterAdapter: EntityAdapter<ListEntry> = createEntityAdapter<ListEntry>();

export interface OrderModifyFormDataState extends EntityState<PCache> {}
export const fdOrderModifyFormDataAdapter: EntityAdapter<PCache> = createEntityAdapter<PCache>();

export interface PCacheState {
  cpInventorySummary: any[];
  cpInventoryDetail: any[];
  cpOrderInquiry: any[];
  cpOrderDetails: any[];
  cpReportList: any[];
  cpUserLookup: any[];
  fltOrderFilters: OrderFilterState;
  fdOrderModify: OrderModifyFormDataState;
}

const initialState: PCacheState = {
  cpInventorySummary: invSum.defColumnPreferences,
  cpInventoryDetail: invDtl.defColumnPreferences,
  cpOrderInquiry: ordInq.defColumnPreferences,
  cpOrderDetails: ordDtl.defColumnPreferences,
  cpReportList: rptList.defColumnPreferences,
  cpUserLookup: usrLkup.defColumnPreferences,
  fltOrderFilters: fltOrderFilterAdapter.getInitialState(),
  fdOrderModify: fdOrderModifyFormDataAdapter.getInitialState(),
};

export function pCacheReducer(state = initialState, action: pcacheActions.PCacheActions) {
  switch (action.type) {
    case pcacheActions.REINIT:
    case authActions.LOGIN_INIT:
      return {
        ...initialState,
        ...state,
      };

    case pcacheActions.INV_SUM_SET_COL_PREF:
      return {
        ...state,
        cpInventorySummary: action.colpref,
      };

    case pcacheActions.INV_SUM_RESET_COL_PREF:
      return {
        ...state,
        cpInventorySummary: invSum.defColumnPreferences,
      };

    case pcacheActions.INV_DTL_SET_COL_PREF:
      return {
        ...state,
        cpInventoryDetail: action.colpref,
      };

    case pcacheActions.INV_DTL_RESET_COL_PREF:
      return {
        ...state,
        cpInventoryDetail: invDtl.defColumnPreferences,
      };

    case pcacheActions.ORD_INQ_SET_COL_PREF:
      return {
        ...state,
        cpOrderInquiry: action.colpref,
      };

    case pcacheActions.ORD_INQ_RESET_COL_PREF:
      return {
        ...state,
        cpOrderInquiry: action.colpref,
      };

    case pcacheActions.ORD_DTL_SET_COL_PREF:
      return {
        ...state,
        cpOrderDetails: action.colpref,
      };

    case pcacheActions.ORD_DTL_RESET_COL_PREF:
      return {
        ...state,
        cpOrderDetails: ordDtl.defColumnPreferences,
      };

    case pcacheActions.REPORT_LIST_SET_COL_PREF:
      return {
        ...state,
        cpReportList: action.colpref,
      };

    case pcacheActions.REPORT_LIST_RESET_COL_PREF:
      return {
        ...state,
        cpReportList: rptList.defColumnPreferences,
      };

    case pcacheActions.USER_LOOKUP_SET_COL_PREF:
      return {
        ...state,
        cpUserLookup: action.colpref,
      };

    case pcacheActions.USER_LOOKUP_RESET_COL_PREF:
      return {
        ...state,
        cpUserLookup: usrLkup.defColumnPreferences,
      };

    case pcacheActions.ORDER_FILTER_UPSERT:
      return {
        ...state,
        fltOrderFilters: fltOrderFilterAdapter.upsertOne(action.listEntry, state.fltOrderFilters),
      };

    case pcacheActions.ORDER_FILTER_DELETE:
      return {
        ...state,
        fltOrderFilters: fltOrderFilterAdapter.removeOne(action.id, state.fltOrderFilters),
      };

    case pcacheActions.ORDER_MODIFY_SET_FORM_DATA:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.upsertOne(action.formdata, state.fdOrderModify),
      };

    case tempdataActions.ORD_MOD_HDR_SET_DATA:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.upsertOne(new PCache('orders/modify/header', action.data), state.fdOrderModify),
      };

    case pcacheActions.ORDER_MODIFY_RESET_FORM_DATA:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.removeOne(action.id, state.fdOrderModify),
      };

    case orderActions.ORD_MOD_HDR_CREATE_INBOUND_SUCCESS:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.removeOne('orders/create/inbound', state.fdOrderModify),
      };

    case orderActions.ORD_MOD_HDR_CREATE_OUTBOUND_SUCCESS:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.removeOne('orders/create/outbound', state.fdOrderModify),
      };

    case authActions.CHANGE_CUSTOMER:
      return {
        ...state,
        fdOrderModify: fdOrderModifyFormDataAdapter.getInitialState(),
      };

    default:
      return state;
  }
}
